// A dropdown row: a parent (Billing, Payments) that opens to list its pages
// beneath it, with ⌄ / ⌃ at the right. In the collapsed sidebar there is no
// room beneath it, so the parent's icon opens its pages in a menu beside it.
import React, { useState } from 'react';
import { Box, Collapse, ListItemButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SidebarNavItem from './SidebarNavItem';
import { IconTile, RowLabel, navRowSx } from './navRow';
import { isItemActive } from './sidebarSections';

export default function SidebarNavGroup({ item, pathname, expanded, onNavigate }) {
  const theme = useTheme();
  const holdsCurrentPage = isItemActive(item, pathname);
  const [open, setOpen] = useState(holdsCurrentPage);
  const [heldCurrentPage, setHeldCurrentPage] = useState(holdsCurrentPage);
  const [flyoutAnchor, setFlyoutAnchor] = useState(null);

  // Arriving on one of its pages opens the dropdown, in this render, so the
  // current page is never shut inside a closed group and the sidebar can
  // scroll to it. Closing it again afterwards is the user's call.
  if (holdsCurrentPage !== heldCurrentPage) {
    setHeldCurrentPage(holdsCurrentPage);
    if (holdsCurrentPage) setOpen(true);
  }

  // The tint and bar mark the parent only while its current page cannot be seen.
  const marked = holdsCurrentPage && (!expanded || !open);
  const listId = `nav-dropdown-${item.key}`;
  const rowSx = { ...navRowSx(theme, { expanded }), ...(holdsCurrentPage && { color: 'primary.main' }) };

  if (!expanded) {
    const closeFlyout = () => setFlyoutAnchor(null);
    return (
      <>
        <Tooltip title={item.label} placement="right">
          <ListItemButton
            selected={marked}
            aria-current={marked ? 'true' : undefined}
            aria-label={item.label}
            aria-haspopup="menu"
            aria-expanded={Boolean(flyoutAnchor)}
            onClick={(e) => setFlyoutAnchor(e.currentTarget)}
            sx={rowSx}
          >
            <IconTile icon={item.icon} highlighted={holdsCurrentPage} />
          </ListItemButton>
        </Tooltip>
        <Menu
          anchorEl={flyoutAnchor}
          open={Boolean(flyoutAnchor)}
          onClose={closeFlyout}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ list: { 'aria-label': item.label }, paper: { sx: { minWidth: 200 } } }}
        >
          <Typography variant="caption" component="li" aria-hidden sx={{ display: 'block', px: 2, pt: 0.5, pb: 1, color: 'text.secondary' }}>
            {item.label}
          </Typography>
          {item.children.map((child) => (
            <MenuItem
              key={child.key}
              selected={isItemActive(child, pathname)}
              onClick={() => { closeFlyout(); onNavigate(child); }}
            >
              {child.label}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  return (
    <>
      <ListItemButton
        selected={marked}
        aria-current={marked ? 'true' : undefined}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => setOpen((isOpen) => !isOpen)}
        sx={rowSx}
      >
        <IconTile icon={item.icon} highlighted={holdsCurrentPage} />
        <RowLabel>{item.label}</RowLabel>
        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box component="ul" id={listId} sx={{ m: 0, p: 0, listStyle: 'none' }}>
          {item.children.map((child) => (
            <Box component="li" key={child.key}>
              <SidebarNavItem item={child} active={isItemActive(child, pathname)} expanded nested onNavigate={onNavigate} />
            </Box>
          ))}
        </Box>
      </Collapse>
    </>
  );
}
